import { useEffect, useRef, useState } from 'react';

function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Reveals `text` progressively so canned answers feel like they are being written.
 * Skips straight to the full string when disabled or when the user prefers reduced motion.
 *
 * @param {string} text
 * @param {boolean} enabled
 * @param {() => void} [onDone]
 */
export function useTypewriter(text, enabled, onDone) {
  const full = String(text || '');
  const [shown, setShown] = useState(enabled ? '' : full);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (!enabled || prefersReducedMotion()) {
      setShown(full);
      doneRef.current?.();
      return undefined;
    }

    setShown('');
    let index = 0;
    /* Reveal in small chunks rather than per character: smooth to read, cheap to render. */
    const step = Math.max(2, Math.round(full.length / 90));
    const timer = window.setInterval(() => {
      index += step;
      if (index >= full.length) {
        window.clearInterval(timer);
        setShown(full);
        doneRef.current?.();
        return;
      }
      setShown(full.slice(0, index));
    }, 16);

    return () => window.clearInterval(timer);
  }, [full, enabled]);

  return shown;
}
